import React, { useState } from "react";
import { Avatar, Button, Card, Typography, Tag, Space } from "antd";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import useAuth from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;

const UserProfile = () => {
  const user = useAuth();
  const userData = JSON.parse(localStorage.getItem("persist:auth"));

  const navigate = useNavigate();

  const profileData = {
    firstName: user?.first_name || "",
    lastName: user?.last_name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    dateOfBirth: user?.date_of_birth || "",
  };

  const InfoItem = ({ icon, label, value }) => (
    <div className="flex items-start gap-3 py-2">
      <div className="text-orange-500 mt-0.5 text-base">{icon}</div>
      <div>
        <Text type="secondary" className="text-xs block leading-none mb-1">
          {label}
        </Text>
        <Text strong className="text-sm text-gray-800">
          {value || "—"}
        </Text>
      </div>
    </div>
  );

  const handleChangePassword = () => {
    // replace this with whatever value you need to supply as the
    // `:token` param (could come from the user object, a hook, etc.)
    const token = userData?.token;

    // navigate to the real path, not the literal string with “:token”
    navigate(`/admin/change-password/${token}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-3xl mx-auto space-y-5">
        {/* ── Back + Page Header ── */}
        <div className="flex items-center gap-3 mb-2">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            className="text-gray-500 hover:text-gray-800 -ml-1"
          />
          <div>
            <Title level={4} className="!mb-0 !text-gray-900">
              My Profile
            </Title>
            <Text type="secondary" className="text-xs">
              Manage your personal information
            </Text>
          </div>
        </div>

        {/* ── Profile Header Card ── */}
        <Card className="border border-gray-100 shadow-sm rounded-xl">
          <div className="flex items-center gap-4">
            <Avatar
              size={64}
              src={user?.avatar}
              icon={!user?.avatar && <UserOutlined />}
              className="border-2 border-orange-200 bg-orange-50 text-orange-500 flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <Title level={5} className="!mb-0.5 !text-gray-900 truncate">
                {profileData.firstName} {profileData.lastName}
              </Title>
              <Text type="secondary" className="text-xs block mb-2">
                {profileData.email}
              </Text>
              <Space size={4} wrap>
                {user?.roles?.map((role) => (
                  <Tag
                    key={role.name}
                    className="text-xs rounded-full border-orange-200 bg-orange-50 text-orange-600 m-0"
                  >
                    {role.name}
                  </Tag>
                ))}
              </Space>
            </div>
          </div>
        </Card>

        {/* ── Personal Information Card ── */}
        <Card
          className="border border-gray-100 shadow-sm rounded-xl"
          title={
            <span className="text-sm font-semibold text-gray-900">
              Personal Information
            </span>
          }
        >
          <div className="border border-dashed border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
              <div className="sm:pr-6">
                <InfoItem
                  icon={<UserOutlined />}
                  label="Full Name"
                  value={`${profileData.firstName} ${profileData.lastName}`}
                />
                <InfoItem
                  icon={<MailOutlined />}
                  label="Email Address"
                  value={profileData.email}
                />
              </div>
              <div className="sm:pl-6 sm:border-l border-gray-100">
                <InfoItem
                  icon={<PhoneOutlined />}
                  label="Phone Number"
                  value={profileData.phone}
                />
                <InfoItem
                  icon={<CalendarOutlined />}
                  label="Date of Birth"
                  value={profileData.dateOfBirth}
                />
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-center">
            <Button
              type="primary"
              icon={<LockOutlined />}
              onClick={handleChangePassword}
              className="bg-orange-500 hover:!bg-orange-600 border-orange-500 hover:!border-orange-600 rounded-full px-6 font-medium"
            >
              Change Password
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default UserProfile;
